import { CommercialUnit } from "../Enums/CommercialUnit";
import { Gender } from "../Enums/Gender";
import { LengthUnit } from "../Enums/LengthUnit";
import { MonthlyServiceFeeStatus } from "../Enums/MonthlyServiceFeeStatus";
import { NfeStatus } from "../Enums/NfeStatus";
import { OrderStatus } from "../Enums/OrderStatus";
import { PaymentMethod } from "../Enums/PaymentMethod";
import { PaymentStatus } from "../Enums/PaymentStatus";
import { ProductOrigin } from "../Enums/ProductOrigin";
import { ReportFormat } from "../Enums/ReportFormat";
import { ShippingMethod } from "../Enums/ShippingMethod";
import { BulkUpdateField } from "../Enums/BulkUpdateField";
import { WeightUnit } from "../Enums/WeightUnit";
import { WhatsAppTemplateType } from "../Enums/WhatsAppTemplateType";
import { WhatsAppMessageStatus } from "../Enums/WhatsAppMessageStatus";

export const getGenderLabel = (gender: Gender): string => {
  switch (gender) {
    case Gender.Male:
      return "Masculino";
    case Gender.Female:
      return "Feminino";
    case Gender.Other:
      return "Outro";
    case Gender.PreferNotToSay:
      return "Prefiro não dizer";
    default:
      return "-";
  }
};

export const getShippingMethodLabel = (method: ShippingMethod | null): string => {
  switch (method) {
    case ShippingMethod.CustomCarrier:
      return "Transportadora a combinar";
    case ShippingMethod.CorreiosPAC:
      return "Correios PAC";
    case ShippingMethod.CorreiosSedex:
      return "Correios SEDEX";
    case ShippingMethod.FreeShipping:
      return "Frete grátis";
    case ShippingMethod.StorePickup:
      return "Retirada na Loja";
    default:
      return "Nenhuma opção selecionada";
  }
};

export const getOrderStatusLabel = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.AwaitingAvailability:
      return "Aguardando disponibilidade";
    case OrderStatus.AwaitingShippingInfo:
      return "Aguardando informações de entrega";
    case OrderStatus.AwaitingPayment:
      return "Aguardando pagamento";
    case OrderStatus.ProcessingPayment:
      return "Pagamento em processamento";
    case OrderStatus.Paid:
      return "Pagamento realizado";
    case OrderStatus.Shipped:
      return "Pedido enviado";
    case OrderStatus.Cancelled:
      return "Pedido cancelado";
    default:
      return "Status desconhecido";
  }
};

export const getPaymentMethodLabel = (method: PaymentMethod | null): string => {
  switch (method) {
    case PaymentMethod.Pix:
      return "PIX";
    case PaymentMethod.BankingBillet:
      return "Boleto bancário";
    case PaymentMethod.CreditCard:
      return "Cartão de crédito";
    case PaymentMethod.Cash:
      return "Dinheiro";
    default:
      return "Método de pagamento desconhecido";
  }
};

export const getPaymentStatusLabel = (status?: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.Paid:
      return "Pago";
    case PaymentStatus.Pending:
      return "Pendente";
    case PaymentStatus.Processing:
      return "Processando";
    case PaymentStatus.Canceled:
      return "Cancelado";
    case PaymentStatus.Failed:
      return "Falhou";
    default:
      return "-";
  }
};

export const getStatusBgColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Paid:
      return "bg-green-100";
    case OrderStatus.AwaitingPayment:
      return "bg-yellow-100";
    case OrderStatus.ProcessingPayment:
      return "bg-blue-100";
    case OrderStatus.Cancelled:
      return "bg-red-100";
    default:
      return "bg-white";
  }
};

export const getNfeStatusLabel = (status: NfeStatus): string => {
  switch (status) {
    case NfeStatus.Pending:
      return "Pendente";
    case NfeStatus.Authorized:
      return "Autorizada";
    case NfeStatus.Rejected:
      return "Rejeitada";
    case NfeStatus.Denied:
      return "Denegada";
    case NfeStatus.Cancelled:
      return "Cancelada";
    case NfeStatus.Closed:
      return "Encerrada";
    case NfeStatus.Error:
      return "Erro";
    default:
      return "Status desconhecido";
  }
};

export const getWhatsAppTemplateTypeLabel = (type: WhatsAppTemplateType): string => {
  switch (type) {
    case WhatsAppTemplateType.RegistrationConfirmation:
      return "Confirmação de cadastro";
    case WhatsAppTemplateType.InStoreRegistration:
      return "Cadastro realizado na loja";
    case WhatsAppTemplateType.ForgotPassword:
      return "Recuperação de senha";
    case WhatsAppTemplateType.VerificationCode:
      return "Código de verificação";
    case WhatsAppTemplateType.OrderCreated:
      return "Pedido criado";
    case WhatsAppTemplateType.PaymentConfirmed:
      return "Pagamento confirmado";
    case WhatsAppTemplateType.OrderCancelled:
      return "Pedido cancelado";
    case WhatsAppTemplateType.NfeEmitted:
      return "NF-e emitida";
    default:
      return "Desconhecido";
  }
};

export const getWhatsAppMessageStatusLabel = (status: WhatsAppMessageStatus): string => {
  switch (status) {
    case WhatsAppMessageStatus.Sent:
      return "Enviada";
    case WhatsAppMessageStatus.Failed:
      return "Falhou";
    default:
      return "Desconhecido";
  }
};

export const getProductOriginOptions = () => ([
  { value: ProductOrigin.National, label: "Nacional" },
  { value: ProductOrigin.ForeignDirectImport, label: "Estrangeira - Importação Direta" },
  { value: ProductOrigin.ForeignPurchasedInDomesticMarket, label: "Estrangeira - Adquirida no Mercado Interno" }
]);

export const getProductCommercialUnitLabel = (unit: CommercialUnit): string => {
  switch (unit) {
    case CommercialUnit.UN:
      return "Unidade (UN)";
    case CommercialUnit.KG:
      return "Quilo (KG)";
    case CommercialUnit.CX:
      return "Caixa (CX)";
    case CommercialUnit.MT:
      return "Metro (MT)";
    case CommercialUnit.LT:
      return "Litro (LT)";
    case CommercialUnit.M2:
      return "Metro Quadrado (M²)";
    case CommercialUnit.M3:
      return "Metro Cúbico (M³)";
    default:
      return "N/I";
  }
};

export const getWeightUnitLabel = (unit: WeightUnit): string => {
  switch (unit) {
    case WeightUnit.G:
      return "g";
    case WeightUnit.KG:
      return "kg";
    default:
      return "kg";
  }
};

export const getLengthUnitLabel = (unit: LengthUnit): string => {
  switch (unit) {
    case LengthUnit.M:
      return "m";
    case LengthUnit.MM:
      return "mm";
    case LengthUnit.CM:
    default:
      return "cm";
  }
};

export const getBulkUpdateFieldOptions = () => ([
  { value: BulkUpdateField.IsActive, label: "Ativo" },
  { value: BulkUpdateField.IsMadeToOrder, label: "Sob encomenda" },
  { value: BulkUpdateField.StockQuantity, label: "Estoque" },
  { value: BulkUpdateField.Ncm, label: "NCM" },
  { value: BulkUpdateField.Barcode, label: "Código de barras" },
  { value: BulkUpdateField.Weight, label: "Peso" },
  { value: BulkUpdateField.Height, label: "Altura" },
  { value: BulkUpdateField.Width, label: "Largura" },
  { value: BulkUpdateField.Length, label: "Comprimento" },
  { value: BulkUpdateField.Price, label: "Preço" }
]);

export const getMonthlyServiceFeeStatusLabel = (status: MonthlyServiceFeeStatus): string => {
  switch (status) {
    case MonthlyServiceFeeStatus.Pending:
      return "Pendente";
    case MonthlyServiceFeeStatus.Billed:
      return "Cobrado";
    case MonthlyServiceFeeStatus.Paid:
      return "Pago";
    default:
      return "Status desconhecido";
  }
};

export const getReportFormatLabel = (format: ReportFormat): string => {
  switch (format) {
    case ReportFormat.Pdf:
      return "PDF";
    case ReportFormat.Csv:
    default:
      return "CSV";
  }
};
